import { useState, useEffect, useRef } from "react";
import axios from "../../config/axiosInstance";
import PageHeader from "../../component/pageHeaders";
import { useNavigate, useLocation } from "react-router-dom";
import { API } from "../../config/API";
import "./styles.css";
import { Button, message, Tabs, DatePicker } from "antd";
import { HiRefresh } from "react-icons/hi";
import NewForm from "./component/newform";
import DataTable from "./component/datatable";
import Loadingbox from "../../component/loadingbox";
import SearchInput from "../../component/SearchInput";
import moment from "moment";

/**
 * GulfVideoScreen Component
 *
 * Manages the listing, filtering, and state of Gulf Videos.
 * Supports:
 * - Tabs for Published/Unpublished
 * - Pagination
 * - Search by Description
 * - Filter by Date
 * - Edit and Soft Delete functionality
 */
function GulfVideoScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isGulfVideo, setIsGulfVideo] = useState(true);
  const [editingRecord, setEditingRecord] = useState<any>(null);
  const [data, setData] = useState<any[]>([]);
  const [meta, setMeta] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(false);

  // Pagination state
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const isInitialMount = useRef(true);

  // Tab state - "published" or "unpublished"
  const [activeTab, setActiveTab] = useState<string>("published");

  // Filter states
  const getFilterParams = () => {
    const query = new URLSearchParams(location.search);
    return {
      search: query.get("search") || "",
      tab: query.get("tab") || "published",
      date: query.get("date") || null,
    };
  };

  const filterParams = getFilterParams();
  const [searchTerm, setSearchTerm] = useState(filterParams.search);
  const [dateFilter, setDateFilter] = useState<any>(
    filterParams.date ? moment(filterParams.date) : null,
  );

  // Effect to load data when URL filters change
  useEffect(() => {
    const params = getFilterParams();

    // Sync filter state with URL
    setSearchTerm(params.search);
    setActiveTab(params.tab);
    setDateFilter(params.date ? moment(params.date) : null);

    // Reset to page 1 when filters change
    setPage(1);

    // Load data with current pagination and new filters
    loadDataWithParams(1, pageSize, params.search, params.tab, params.date);
  }, [location.search]);

  // Effect to load data when pagination changes
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    const params = getFilterParams();
    loadDataWithParams(page, pageSize, params.search, params.tab, params.date);
  }, [page, pageSize]);

  const transformData = (backendData: any[]) => {
    return backendData.map((item: any) => {
      const attrs = item.attributes || item;
      const id = item.id;
      return {
        id: id,
        video_id: attrs.video_id || null,
        description: attrs.description || null,
        createdAt: attrs.createdAt || null,
        updatedAt: attrs.updatedAt || null,
        is_published: attrs.is_published || false,
        is_deleted: attrs.is_deleted || false,
        user:
          attrs.user && attrs.user.data
            ? {
                id: attrs.user.data.id,
                username: attrs.user.data.attributes?.username || "N/A",
              }
            : attrs.user
              ? {
                  id: attrs.user.id,
                  username: attrs.user.username || "N/A",
                }
              : null,
      };
    });
  };

  /**
   * Fetches data from API with applied filters
   */
  const loadDataWithParams = (
    currentPage: number,
    currentPageSize: number,
    searchTerm: string = "",
    tab: string = "published",
    dateFilterStr: string | null = null,
  ) => {
    setIsLoading(true);

    // Build query params
    let params = new URLSearchParams();
    params.append("pagination[page]", String(currentPage));
    params.append("pagination[pageSize]", String(currentPageSize));
    params.append("sort[0]", "createdAt:desc");
    params.append("filters[is_deleted][$eq]", "false");

    // Filter by published status based on active tab
    if (tab === "published") {
      params.append("filters[is_published][$eq]", "true");
    } else {
      params.append("filters[is_published][$eq]", "false");
    }

    // Search filter
    if (searchTerm) {
      params.append("filters[description][$containsi]", searchTerm);
    }

    // Date filter
    if (dateFilterStr) {
      const startOfDay = moment(dateFilterStr).startOf("day").toISOString();
      const endOfDay = moment(dateFilterStr).endOf("day").toISOString();
      params.append("filters[createdAt][$gte]", startOfDay);
      params.append("filters[createdAt][$lte]", endOfDay);
    }

    let url = `${API.BASE_URL}${API.GULF_VIDEO}?${params.toString()}`;

    axios
      .get(url)
      .then((response) => {
        let backendData = response.data.data || [];
        let transformedData = transformData(backendData);
        setData(transformedData);
        const total = response.data.meta?.pagination?.total || 0;
        setMeta(total);
        setIsLoading(false);
      })
      .catch((err) => {
        console.log(err);
        setIsLoading(false);
        message.error("Failed to load data");
      });
  };

  const loadData = () => {
    const params = getFilterParams();
    loadDataWithParams(page, pageSize, params.search, params.tab, params.date);
  };

  const updateFiltersInUrl = (
    updates: Partial<{ search: string; tab: string; date: string | null }>,
  ) => {
    const currentQuery = new URLSearchParams(location.search);

    if (updates.search !== undefined) {
      if (updates.search) {
        currentQuery.set("search", updates.search);
      } else {
        currentQuery.delete("search");
      }
    }
    if (updates.tab !== undefined) {
      currentQuery.set("tab", updates.tab);
    }
    if (updates.date !== undefined) {
      if (updates.date) {
        currentQuery.set("date", updates.date);
      } else {
        currentQuery.delete("date");
      }
    }

    navigate({ search: currentQuery.toString() }, { replace: false });
  };

  const onPageChange = (newPage: number, newPageSize: number) => {
    setPage(newPage);
    setPageSize(newPageSize);
    window.scrollTo(0, 0);
  };

  const handleDelete = async (record: any) => {
    let url = `${API.BASE_URL}${API.GULF_VIDEO}/${record.id}`;
    try {
      await axios.delete(url);
      message.success("Item Deleted");
      const remainingItems = data.filter((item) => item.id !== record.id);
      setData(remainingItems);
      setMeta((prev) => Math.max(0, prev - 1));
    } catch (e) {
      message.error("Something went wrong");
    }
  };

  const handleEdit = (record: any) => {
    setEditingRecord(record);
    setIsGulfVideo(false);
  };

  const handleSearch = (value: string) => {
    updateFiltersInUrl({ search: value });
  };

  const handleTabChange = (key: string) => {
    setPage(1);
    updateFiltersInUrl({ tab: key });
  };

  const handleDateFilter = (date: any, dateString: string | string[]) => {
    const dateValue = Array.isArray(dateString) ? dateString[0] : dateString;
    updateFiltersInUrl({ date: dateValue || null });
  };

  const handleTogglePublish = async (
    record: any,
    checked: boolean,
    onComplete: () => void,
  ) => {
    try {
      const url = `${API.BASE_URL}${API.GULF_VIDEO}/${record.id}`;
      const requestObj = {
        data: {
          is_published: checked,
        },
      };

      const response = await axios.put(url, requestObj);

      if (response.data?.data) {
        message.success(
          checked ? "Published successfully" : "Unpublished successfully",
        );
        // Remove from current list since it will move to another tab
        const remainingItems = data.filter((item) => item.id !== record.id);
        setData(remainingItems);
        setMeta((prev) => Math.max(0, prev - 1));
        onComplete();
      } else {
        throw new Error("Invalid response from server");
      }
    } catch (error: any) {
      console.error("Error toggling publish status:", error);
      message.error("Failed to update status");
      loadData();
      onComplete();
    }
  };

  const tabItems = [
    {
      key: "published",
      label: "Published",
    },
    {
      key: "unpublished",
      label: "Unpublished",
    },
  ];

  return (
    <>
      <div>
        {isGulfVideo && (
          <PageHeader
            title={"Gulf Videos"}
            length={`${meta || 0} Videos`}
            loader={false}
            onBack={() => navigate("/Auth/")}
          >
            <div>
              <Button
                type="primary"
                ghost
                icon={<HiRefresh />}
                onClick={() => loadData()}
              >
                Refresh
              </Button>
            </div>
            <div
              style={{
                display: "flex",
                gap: "10px",
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <SearchInput
                onSearch={handleSearch}
                placeholder="Search description..."
              />
              {/* <DatePicker
                onChange={handleDateFilter}
                value={dateFilter}
                placeholder="Filter by Date"
              /> */}

              <Button
                type="primary"
                onClick={() => {
                  setEditingRecord(null);
                  setIsGulfVideo(false);
                }}
              >
                Add New +
              </Button>
            </div>
          </PageHeader>
        )}

        {isGulfVideo ? (
          <>
            <div style={{ padding: "0 16px", marginTop: 16 }}>
              <Tabs
                activeKey={activeTab}
                onChange={handleTabChange}
                items={tabItems}
                type="card"
                size="large"
              />
            </div>
            {isLoading ? (
              <Loadingbox />
            ) : (
              <>
                <div style={{ padding: 4 }} />
                <DataTable
                  data={data}
                  meta={meta}
                  page={page}
                  pagesize={pageSize}
                  loading={isLoading}
                  refresh={() => loadData()}
                  onDelete={(val: any) => handleDelete(val)}
                  onEdit={handleEdit}
                  onTogglePublish={handleTogglePublish}
                  onPageChange={onPageChange}
                  activeTab={activeTab}
                />
              </>
            )}
          </>
        ) : (
          <NewForm
            onClose={() => {
              setIsGulfVideo(true);
              loadData();
            }}
            reload={() => loadData()}
            record={editingRecord}
          />
        )}
      </div>
      <br />
      <br />
      <br />
    </>
  );
}

export default GulfVideoScreen;
